/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * AssignmentProgressCardStyles.js
 * Styles for assignment progress card components
 */

import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CustomCard from '../../common/CustomCard';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { Badge } from '../../tabular/styles/TabularStyles';
import {
  SectionsContainer,
  SectionsLabel,
  SectionChip,
  SectionNumber,
  TextContainer
} from '../../tabular/styles/TemplateStyles';

export const AssignmentProgressCardRoot = styled(CustomCard)({
  '&&': {
    '&.theme-dark': {
      '--card-bg': mixins.darkThemeBgColor,
      '--card-text': mixins.darkThemeTextColorPrimary,
      '--card-bg-2': mixins.darkThemeBgColor2,
      '--muted-text': mixins.darkThemeTextColorTertiary,
      '--progress-bar-bg': mixins.darkProgressBgColor
    },
    '&.theme-light': {
      '--card-bg': mixins.chalkWhiteFont,
      '--card-text': mixins.color_text_primary,
      '--card-bg-2': mixins.lightThemeBgColor2,
      '--muted-text': mixins.zambezi,
      '--progress-bar-bg': mixins.quaternary
    },

    borderRadius: 16,
    backgroundColor: 'var(--card-bg)',
    '&&&': {
      backgroundColor: 'var(--card-bg)',
      background: 'var(--card-bg)'
    },
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minWidth: 366,
    width: '100%',

    '& *': {
      boxSizing: 'border-box'
    },
    '& > *': {
      width: '100%'
    }
  }
});

export const AssignmentProgressCardHeader = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    minHeight: 44,
    minWidth: 0
  }
});

export const AssignmentProgressCardTitleContainer = styled('div')({
  '&&': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',

    '& > *': {
      display: 'block',
      minWidth: 0,
      overflow: 'hidden'
    }
  }
});

export const AssignmentProgressCardStatusContainer = styled('div')({
  '&&': {
    flexShrink: 0
  }
});

export const AssignmentProgressCardFooter = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  }
});

export const AssignmentProgressCardTitle = styled('span')({
  '&&': {
    color: 'var(--card-text)',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '22px',
    minWidth: 0,
    ...CommonStyles().truncateNumberOfLines(2)
  }
});

export const AssignmentProgressCardContent = styled('div')({
  '&&': {
    padding: '4px 0px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  }
});

export const AssignmentProgressCardSectionsContainer = styled(SectionsContainer)({
  '&&': {
    flexWrap: 'wrap',
    marginTop: 0
  }
});

export const AssignmentProgressCardSectionsLabel = styled(SectionsLabel)({
  '&&': {
    ...CommonStyles().assignmentCardSectionsLabel,
    color: 'var(--card-text)'
  },
  '&&&.MuiTypography-body1': {
    ...CommonStyles().assignmentCardSectionsLabel,
    color: 'var(--card-text)'
  }
});

export const AssignmentProgressCardSectionChip = styled(SectionChip)({
  '&&': {
    background: 'var(--card-bg-2)'
  }
});

export const AssignmentProgressCardSectionNumber = styled(SectionNumber)({
  '&&': {
    color: 'var(--card-text)'
  }
});

export const AssignmentProgressCardSectionTextContainer = styled(TextContainer)({
  '&&': {
    padding: 0
  }
});

export const AssignmentProgressCardFooterRightText = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    flexShrink: 0
  }
});

export const AssignmentProgressCardFooterRightTextLine = styled(Typography)({
  '&&': {
    color: 'var(--muted-text)',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '20px',
    textAlign: 'right'
  }
});

export const AssignmentProgressCardBadge = styled(Badge)({
  '&&': {
    borderRadius: 4
  }
});

export const AssignmentProgressCardFooterBadge = styled(AssignmentProgressCardBadge)({
  '&&': {
    backgroundColor: 'var(--card-bg-2) !important',
    color: 'var(--card-text) !important',
    width: 'fit-content',
    flexShrink: 0,
    maxHeight: mixins.lineHeight22,
    marginTop: 4,
    '& .MuiChip-label': {
      paddingLeft: 4,
      paddingRight: 4,
      paddingTop: 0,
      paddingBottom: 2,
      color: 'var(--card-text)',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize12,
      fontWeight: mixins.fontWeight500,
      lineHeight: '20px'
    }
  }
});

export const AssignmentProgressCardProgressBar = styled('div')({
  '&&': {
    width: '100%',
    cursor: 'pointer'
  }
});

export const AssignmentProgressCardProgressBarContainer = styled('div')({
  '&&': {
    minHeight: 10
  }
});

export const AssignmentProgressCardProgressBarWrapperButton = styled('button')({
  '&&': {
    ...CommonStyles().buttonReset,
    display: 'block',
    width: '100%'
  }
});

export const assignmentProgressCardTemplateComponents = {
  sectionsContainer: AssignmentProgressCardSectionsContainer,
  sectionsLabel: AssignmentProgressCardSectionsLabel,
  sectionChip: AssignmentProgressCardSectionChip,
  sectionNumber: AssignmentProgressCardSectionNumber,
  textContainer: AssignmentProgressCardSectionTextContainer,
  badge: AssignmentProgressCardBadge,
  footerBadge: AssignmentProgressCardFooterBadge
};
